import { CloseModalButton, SecondaryButton } from '@library/components/buttons';
import {
  CorrelationsDetails,
  CorrelationsHeader
} from '@bo-components/correlations';
import type { ComboboxOption } from '@library/components/selects';
import { Modal } from '@library/components/modals/Modal';
import { SubmitResetBar } from '@library/components/SubmitResetBar';
import { getDisableSave } from '@bo-utils/correlations';
import { useAddCorrelations } from '@bo-hooks/correlations/useAddCorrelations';

export interface AddCorrelationsModalProps {
  allStatTypes: Array<ComboboxOption>;
  handleCorrelationsRefetch: () => Promise<void>;
  handleResetCorrelations: () => void;
  isOpen: boolean;
  onCancel: () => void;
}

export const AddCorrelationsModal = ({
  allStatTypes,
  handleCorrelationsRefetch,
  handleResetCorrelations,
  isOpen,
  onCancel
}: AddCorrelationsModalProps) => {
  const {
    addCorrelationsStatus: { errors, isError, isLoading, isSuccess, message },
    newCorrelations: correlations,
    handleAddCorrelation,
    handleCorrelationsChange,
    handleDeleteCorrelation,
    handlePublishCorrelations,
    handleResetAddCorrelationsStatus
  } = useAddCorrelations({
    handleCorrelationsRefetch,
    handleResetCorrelations
  });

  const disableSave = getDisableSave({ correlations });

  const handleAddCorrelations = () => {
    handleResetAddCorrelationsStatus();
    handleAddCorrelation();
  };

  return (
    <Modal isOpen={isOpen} onCancelModal={onCancel}>
      <div className="bg-soFresh-110 border-soFresh-140 relative mx-auto min-h-96 w-auto min-w-[55rem] flex-col rounded-md border">
        <div className="border-soFresh-140 bg-soFresh-130 flex h-16 items-start justify-between rounded-t border-b border-solid p-4">
          <h2>Add Correlations</h2>
          <CloseModalButton handleOnClick={onCancel} />
        </div>
        <div className="w-full overflow-auto p-4">
          {correlations.length > 0 && (
            <table className="w-full table-auto">
              <CorrelationsHeader isNewRowsOnly />
              <CorrelationsDetails
                allStatTypes={allStatTypes}
                errors={errors}
                newCorrelations={correlations}
                isNewRowsOnly
                onAddCorrelationsChange={handleCorrelationsChange}
                onDeleteCorrelation={handleDeleteCorrelation}
              />
            </table>
          )}
          <div className="py-6">
            <SecondaryButton
              handleOnClick={handleAddCorrelations}
              title="Add Correlation"
            >
              + Add Correlation
            </SecondaryButton>
          </div>
          {correlations.length > 0 && (
            <SubmitResetBar
              cancelText="Cancel"
              handlePublish={() => handlePublishCorrelations({ correlations })}
              handleReset={onCancel}
              saveDisabled={disableSave || isLoading}
            />
          )}
        </div>
        {isError && (
          <p className="text-roses p-6">{`Error: ${message ?? ''}`}</p>
        )}
        {isSuccess && (
          <p className="text-atlien-200 p-6">
            Correlations Published Successfully
          </p>
        )}
      </div>
    </Modal>
  );
};
